import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  FormLayout,
  Spinner,
  Button,
  AggregatorDropdown,
} from 'components/common'
import toast from 'react-hot-toast'
import StoreDropdown from 'components/userManagement/storeDropdown'
import BarcodeImportButton from 'components/common/dataTable/barcodeImportButton'
import Api from 'utils/api/axiosInstance'
import { FUNCTION_JOBS } from 'utils/constants'
import { usePageAccess } from 'hooks'
import { ImportColumn } from 'components/common/importModal'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'

// API function for barcode file upload
export const uploadBarcodeFileApi = async (data: any) => {
  return Api.post({
    url: FUNCTION_JOBS.IMPORT_FTP_BARCODES,
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// API function for export
export const exportBarcodeDataApi = async (data: any) => {
  return Api.post({
    url: FUNCTION_JOBS.EXPORT_FTP_BARCODES,
    data: data,
  })
}

const FtpSelectedBarcode = () => {
  const [loading] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [ftpImportColumns, setFtpImportColumns] = useState<ImportColumn[]>([])
  const [isExporting, setIsExporting] = useState<boolean>(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const { isEditor } = usePageAccess()

  useEffect(() => {
    getImportTemplate('ftpBarcodes')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setFtpImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadFtpSample = async () => {
    const response = await downloadImportSample('ftpBarcodes')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'ftp_barcodes_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  // Form state without complex validation
  const { setValue, watch } = useForm()

  // Watch for store and aggregator selections
  const selectedStores = watch('stores')
  const selectedAggregators = watch('aggregators')

  // Simple extraction of ID values
  const getStoreId = () => {
    if (!selectedStores) return null
    return selectedStores
  }

  const getAggregatorId = () => {
    if (!selectedAggregators) return null
    return selectedAggregators
  }

  // Are selections valid
  const hasValidSelections = () => {
    return getStoreId() && getAggregatorId()
  }

  // Handle file selection
  const handleFileSelected = (file: File | null) => {
    setSelectedFile(file)
  }

  // Handle export button click
  const handleExport = async () => {
    const storeId = getStoreId()
    const aggregatorId = getAggregatorId()

    if (!storeId || !aggregatorId) {
      toast.error('Please select store and aggregator before exporting')
      return
    }

    setIsExporting(true)
    const exportToast = toast.loading('Exporting data...', {
      duration: Infinity,
    })

    try {
      // Send export request with store and aggregator IDs
      await exportBarcodeDataApi({
        storeId: String(storeId),
        aggregatorId: String(aggregatorId),
      })

      toast.success('Data Exported Successfully', {
        duration: 3000,
        id: exportToast,
      })
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Error Exporting Data', {
        duration: 5000,
        id: exportToast,
      })
    } finally {
      setIsExporting(false)
    }
  }

  // Handle form submission with file upload
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedFile) {
      toast.error('Please select a file before submitting')
      return
    }

    const storeId = getStoreId()
    const aggregatorId = getAggregatorId()

    if (!storeId || !aggregatorId) {
      toast.error('Please select store and aggregator before submitting')
      return
    }

    // Start uploading file in chunks
    setIsSubmitting(true)
    const uploadToast = toast.loading('Uploading file...', {
      duration: Infinity,
    })

    try {
      await uploadFileInChunks(selectedFile, storeId, aggregatorId, uploadToast)
      toast.success('File Uploaded Successfully', {
        duration: 3000,
        id: uploadToast,
      })
    } catch (error) {
      console.error('Upload error:', error)
      toast.error('Error Uploading File', {
        duration: 5000,
        id: uploadToast,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Function to upload file in chunks
  const uploadFileInChunks = async (
    file: File,
    storeId: string,
    aggregatorId: string,
    toastId: string,
  ) => {
    const chunkSize = 5 * 1024 * 1024 // 5MB chunks
    const totalChunks = Math.ceil(file.size / chunkSize)

    let chunkNumber = 0
    let start = 0
    let end = 0

    const uploadNextChunk = async () => {
      start = end
      end = Math.min(end + chunkSize, file.size)
      chunkNumber += 1

      // Create the chunk
      const chunk = file.slice(start, end)

      // Create FormData with all required info
      const formData = new FormData()
      formData.append('file', chunk)
      formData.append('originalname', file.name)
      formData.append('chunkNumber', chunkNumber.toString())
      formData.append('totalChunks', totalChunks.toString())
      formData.append('fileSize', file.size.toString())

      // Add store and aggregator IDs - ensuring they're strings
      formData.append('storeId', String(storeId))
      formData.append('aggregatorId', String(aggregatorId))

      const response = await uploadBarcodeFileApi(formData)

      if (response.status === 200) {
        // Update toast message with progress
        toast.loading(
          `Uploading: ${Math.round((chunkNumber / totalChunks) * 100)}%`,
          {
            id: toastId,
          },
        )

        // Continue uploading chunks or finish
        if (chunkNumber < totalChunks) {
          await uploadNextChunk()
        }
      } else {
        throw new Error('Failed to upload chunk')
      }
    }

    // Start the upload process
    await uploadNextChunk()
  }

  return (
    <FormLayout className="mt-4 rounded-lg border border-gray-100 bg-white shadow-sm">
      <form onSubmit={handleSubmit}>
        {loading ? (
          <Spinner />
        ) : (
          <div className="grid grid-cols-2 gap-10">
            <div>
              <h4 className="text-xl font-semibold text-gray-800">
                Selected Barcodes For FTP Cron
              </h4>
              <p className="mt-2 text-sm text-gray-500">
                Select a store and aggregator first, then choose to import or
                export data
              </p>

              {/* Common Selection Area */}

              <div className="mb-4 flex flex-wrap gap-x-8 gap-y-4">
                <div className="w-full max-w-xs">
                  <StoreDropdown
                    required
                    label="Store"
                    setValue={setValue}
                    name="stores"
                  />
                </div>
                <div className="w-full max-w-xs">
                  <AggregatorDropdown
                    required
                    label="Aggregator"
                    setValue={setValue}
                    name="aggregators"
                  />
                </div>
              </div>

              {/* Import Section */}
              {isEditor && (
                <div>
                  <h5 className="mb-3 font-medium text-gray-700">
                    Import Barcodes
                  </h5>
                  <div className="flex flex-col">
                    <div>
                      <BarcodeImportButton
                        onFileSelected={handleFileSelected}
                        disabled={!hasValidSelections() || isSubmitting}
                        importColumns={ftpImportColumns}
                        importModuleName="FTP selected barcodes"
                        onDownloadSample={handleDownloadFtpSample}
                      />
                      {selectedFile && (
                        <p className="mt-2 flex items-center text-sm font-medium text-green-600">
                          <svg
                            className="mr-1 h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          File selected: {selectedFile.name} (
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                    </div>
                    <div className="mt-3">
                      <Button
                        buttonText={
                          isSubmitting ? 'Uploading...' : 'Submit Import'
                        }
                        type="submit"
                        disabled={
                          !selectedFile ||
                          !hasValidSelections() ||
                          isSubmitting ||
                          isExporting
                        }
                        loading={isSubmitting}
                      />
                      {!selectedFile && hasValidSelections() && (
                        <p className="mt-2 text-xs text-gray-500">
                          Please select a barcode CSV file before submitting.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Export Section */}
                </div>
              )}
            </div>

            {/* Export */}
            <div>
              <h5 className="font-medium text-gray-700">Export Data</h5>
              <div className="flex flex-col">
                <p className="mb-3 text-sm text-gray-600">
                  Export barcode data for the selected store and aggregator
                </p>
                <div>
                  <Button
                    buttonText={isExporting ? 'Exporting...' : 'Export Data'}
                    type="button"
                    onClick={handleExport}
                    disabled={
                      !hasValidSelections() || isSubmitting || isExporting
                    }
                    loading={isExporting}
                  />
                  {!hasValidSelections() && (
                    <p className="mt-2 text-xs text-gray-500">
                      Store and aggregator selection required for export.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </FormLayout>
  )
}

export default FtpSelectedBarcode
